import React, { useMemo, useState } from 'react';
import {
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Theme,
  Typography,
  alpha,
  useMediaQuery,
} from '@mui/material';
import { LogOut, MessageSquare, UserCircle2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useLogout } from '../hooks';
import { PROFILE_ROUTE } from '../profile';
import { UserAvatar } from '../../../shared/components';
import { Store } from '../../../redux';
import { DM_PAGE_ROUTE } from '../chat';
import { useDmPageQuery } from '../chat/dm';

export interface ProfileButtonProps {
  hideLogout?: boolean;
}

const MENU_ITEM_SX = (theme: Theme) => ({
  py: 1.25,
  px: 2,
  gap: 1.25,
  borderRadius: 1,
  mx: 0.5,
  my: 0.25,
  fontSize: '0.875rem',
  fontWeight: 500,
  color: theme.palette.text.primary,
  '&:hover': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.06)'
        : 'rgba(0, 0, 0, 0.04)',
  },
});

const MENU_ICON_SX = {
  minWidth: 'auto',
  color: 'inherit',
} as const;

export const ProfileButton: React.FC<ProfileButtonProps> = ({
  hideLogout = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isMenuOpen = Boolean(anchorEl);

  const { logout } = useLogout();
  const nav = useNavigate();

  const isMobile = useMediaQuery<Theme>(theme =>
    theme.breakpoints.down('sm')
  );

  const userName = useSelector((s: Store) => s.userReducer.name);
  const userEmail = useSelector((s: Store) => s.userReducer.email);
  const firebaseUID = useSelector((s: Store) => s.userReducer.firebaseUID);

  // Only subscribe to unread DMs when we're going to render the Messages
  // entry in the dropdown (mobile). On larger screens ChatNotification has
  // its own dedicated top-bar slot and we don't want to double-mount the
  // Firestore listener here.
  const { pageData } = useDmPageQuery(false);
  const unreadCount = useMemo(() => {
    if (!isMobile) return 0;
    const threads = pageData?.uniqueMessagePerUser ?? [];
    return threads.filter(
      // A thread is "unread for me" only when the last message was sent by
      // the OTHER party AND they haven't been marked as read yet. Without the
      // sender check we'd count messages I sent myself as unread (false alarm).
      m => m.senderID !== firebaseUID && m.isRead === 'false'
    ).length;
  }, [isMobile, pageData, firebaseUID]);

  const handleMenuClose = () => setAnchorEl(null);
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget);

  return (
    <>
      <IconButton
        aria-label="Account menu"
        aria-haspopup="menu"
        aria-expanded={isMenuOpen}
        onClick={handleMenuOpen}
        edge="end"
        sx={{ p: 0.5, width: 44, height: 44 }}
      >
        <UserAvatar
          hideState
          fetchCurrentUser
          avatarProps={{
            sx: {
              width: 32,
              height: 32,
            },
          }}
        />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        keepMounted
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              minWidth: 220,
              borderRadius: 2,
              boxShadow: 'var(--bh-shadow)',
              overflow: 'hidden',
              border: theme => `1px solid ${theme.palette.divider}`,
            },
          },
        }}
      >
        {/* Identity header */}
        {(userName || userEmail) && (
          <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
            {userName && (
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: theme => theme.palette.text.primary,
                  lineHeight: 1.2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {userName}
              </Typography>
            )}
            {userEmail && (
              <Typography
                variant="caption"
                sx={{
                  color: theme => theme.palette.text.secondary,
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {userEmail}
              </Typography>
            )}
          </Box>
        )}
        {(userName || userEmail) && <Divider sx={{ my: 0.5 }} />}

        <MenuItem
          sx={MENU_ITEM_SX}
          onClick={() => {
            nav(PROFILE_ROUTE.ABSOLUTE_PATH);
            handleMenuClose();
          }}
        >
          <ListItemIcon sx={MENU_ICON_SX}>
            <UserCircle2 size={18} />
          </ListItemIcon>
          Profile
        </MenuItem>

        {/* Messages is exposed in the dropdown only on small screens — on
            larger screens ChatNotification keeps its dedicated top-bar slot. */}
        {isMobile && (
          <MenuItem
            sx={MENU_ITEM_SX}
            onClick={() => {
              nav(DM_PAGE_ROUTE.ABSOLUTE_PATH);
              handleMenuClose();
            }}
          >
            <ListItemIcon sx={MENU_ICON_SX}>
              <MessageSquare size={18} />
            </ListItemIcon>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                flex: 1,
              }}
            >
              <span>Messages</span>
              {unreadCount > 0 && (
                <Box
                  component="span"
                  sx={theme => ({
                    minWidth: 18,
                    height: 18,
                    px: 0.5,
                    borderRadius: 9,
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    color: theme.palette.success.contrastText,
                    backgroundColor: theme.palette.success.main,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  })}
                >
                  {unreadCount}
                </Box>
              )}
            </Box>
          </MenuItem>
        )}

        {!hideLogout && (
          <>
            <Divider sx={{ my: 0.5 }} />
            <MenuItem
              sx={theme => ({
                ...MENU_ITEM_SX(theme),
                color: theme.palette.error.main,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.error.main, 0.1),
                },
              })}
              onClick={() => {
                logout();
                handleMenuClose();
              }}
            >
              <ListItemIcon sx={{ ...MENU_ICON_SX, color: 'inherit' }}>
                <LogOut size={18} />
              </ListItemIcon>
              Log out
            </MenuItem>
          </>
        )}
      </Menu>
    </>
  );
};
